import Phaser from 'phaser';
import { GAME_CONFIG } from '../config/gameConfig';
import { ScoreService } from '../services/ScoreService';

/**
 * TitleScene: Animated title screen with neon logo,
 * marching aliens in background, and pulsing "Press Start" prompt.
 */
export class TitleScene extends Phaser.Scene {
  private pressStartText!: Phaser.GameObjects.Text;
  private pressStartGlow!: Phaser.GameObjects.Text;
  private titleText!: Phaser.GameObjects.Text;
  private titleGlow!: Phaser.GameObjects.Text;
  private subtitleText!: Phaser.GameObjects.Text;
  private subtitleGlow!: Phaser.GameObjects.Text;
  private pulseTimer = 0;
  private titleBaseY = 0;
  private isTransitioning = false;

  constructor() {
    super({ key: 'TitleScene' });
  }

  /**
   * Safely transition to another scene, preventing duplicate transitions
   * from rapid clicks or key presses.
   */
  private safeSceneStart(sceneKey: string, data?: object): void {
    if (this.isTransitioning) return;
    this.isTransitioning = true;
    this.scene.start(sceneKey, data);
  }

  create(): void {
    this.isTransitioning = false;
    const { width, height } = this.cameras.main;
    this.titleBaseY = height / 4;

    // Subtle dark blue gradient overlay for depth
    const gradientGfx = this.add.graphics();
    gradientGfx.setDepth(-2);
    const steps = 20;
    for (let s = 0; s < steps; s++) {
      const t = s / steps;
      const alpha = 0.12 * (1 - t);
      gradientGfx.fillStyle(0x0a1030, alpha);
      gradientGfx.fillRect(0, (height / steps) * s, width, height / steps + 1);
    }

    // Starfield background
    for (let i = 0; i < 100; i++) {
      const star = this.add.image(
        Phaser.Math.Between(0, width),
        Phaser.Math.Between(0, height),
        'star'
      );
      star.setAlpha(Phaser.Math.FloatBetween(0.3, 1.0));

      // Subtle twinkling animation
      this.tweens.add({
        targets: star,
        alpha: Phaser.Math.FloatBetween(0.1, 0.5),
        duration: Phaser.Math.Between(1500, 4000),
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }

    // === Title text with neon glow effect ===
    // Glow layer (behind, larger, blurred via alpha and color)
    this.titleGlow = this.add.text(width / 2, this.titleBaseY, 'SPACE\nINVADERS', {
      fontSize: '52px',
      color: '#00ffff',
      fontFamily: '"Press Start 2P", monospace',
      align: 'center',
      lineSpacing: 10,
      stroke: '#00ffff',
      strokeThickness: 8,
    });
    this.titleGlow.setOrigin(0.5, 0.5);
    this.titleGlow.setAlpha(0.3);

    // Main title text
    this.titleText = this.add.text(width / 2, this.titleBaseY, 'SPACE\nINVADERS', {
      fontSize: '48px',
      color: '#00ffff',
      fontFamily: '"Press Start 2P", monospace',
      align: 'center',
      lineSpacing: 10,
      stroke: '#006688',
      strokeThickness: 2,
      shadow: {
        offsetX: 0,
        offsetY: 0,
        color: '#00ffff',
        blur: 20,
        fill: true,
        stroke: true,
      },
    });
    this.titleText.setOrigin(0.5, 0.5);

    // Title floating animation
    this.tweens.add({
      targets: [this.titleText, this.titleGlow],
      y: this.titleBaseY - 5,
      duration: 2000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // Title glow pulsing animation
    this.tweens.add({
      targets: this.titleGlow,
      alpha: { from: 0.2, to: 0.5 },
      duration: 1500,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // === Subtitle with glow ===
    this.subtitleGlow = this.add.text(width / 2, this.titleBaseY + 80, 'NEON EDITION', {
      fontSize: '18px',
      color: '#ff00ff',
      fontFamily: '"Press Start 2P", monospace',
      stroke: '#ff00ff',
      strokeThickness: 6,
    });
    this.subtitleGlow.setOrigin(0.5, 0.5);
    this.subtitleGlow.setAlpha(0.25);

    this.subtitleText = this.add.text(width / 2, this.titleBaseY + 80, 'NEON EDITION', {
      fontSize: '16px',
      color: '#ff00ff',
      fontFamily: '"Press Start 2P", monospace',
      shadow: {
        offsetX: 0,
        offsetY: 0,
        color: '#ff00ff',
        blur: 15,
        fill: true,
        stroke: true,
      },
    });
    this.subtitleText.setOrigin(0.5, 0.5);

    // Subtitle glow pulse
    this.tweens.add({
      targets: this.subtitleGlow,
      alpha: { from: 0.15, to: 0.4 },
      duration: 2000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // Animated background aliens (decorative)
    this.createBackgroundAliens();

    // === Press Start prompt with neon pulsing ===
    // Glow layer for Press Start
    this.pressStartGlow = this.add.text(width / 2, height * 0.65, 'PRESS START', {
      fontSize: '22px',
      color: '#ffffff',
      fontFamily: '"Press Start 2P", monospace',
      stroke: '#00ffff',
      strokeThickness: 8,
    });
    this.pressStartGlow.setOrigin(0.5, 0.5);
    this.pressStartGlow.setAlpha(0.2);

    // Main Press Start text
    this.pressStartText = this.add.text(width / 2, height * 0.65, 'PRESS START', {
      fontSize: '20px',
      color: '#ffffff',
      fontFamily: '"Press Start 2P", monospace',
      shadow: {
        offsetX: 0,
        offsetY: 0,
        color: '#00ffff',
        blur: 15,
        fill: true,
        stroke: true,
      },
    });
    this.pressStartText.setOrigin(0.5, 0.5);

    // High Score button with neon styling
    const highScoreText = this.add.text(width / 2, height * 0.78, 'HIGH SCORES', {
      fontSize: '14px',
      color: '#00ff41',
      fontFamily: '"Press Start 2P", monospace',
      shadow: {
        offsetX: 0,
        offsetY: 0,
        color: '#00ff41',
        blur: 10,
        fill: true,
        stroke: true,
      },
    });
    highScoreText.setOrigin(0.5, 0.5);
    highScoreText.setInteractive({ useHandCursor: true });
    highScoreText.on('pointerover', () => {
      selectedMenu = 1;
      updateTitleMenuHighlight();
    });
    highScoreText.on('pointerout', () => {
      updateTitleMenuHighlight();
    });
    highScoreText.on('pointerdown', () => {
      this.safeSceneStart('HighScoreScene');
    });

    // Keyboard menu navigation state
    let selectedMenu = 0; // 0 = start, 1 = high scores
    const menuTexts = [this.pressStartText, highScoreText];
    const menuGlows = [this.pressStartGlow, null]; // pressStartGlow for item 0
    const menuDefaults = [
      { color: '#ffffff', glowColor: '#00ffff', glowBlur: 15 },
      { color: '#00ff41', glowColor: '#00ff41', glowBlur: 10 },
    ];
    const menuItems = [
      { select: () => this.startGame() },
      { select: () => this.safeSceneStart('HighScoreScene') },
    ];

    // Focus arrow indicator (▶) showing currently selected item
    const focusArrow = this.add.text(0, 0, '▶', {
      fontSize: '16px',
      color: '#00ffff',
      fontFamily: '"Press Start 2P", monospace',
      shadow: {
        offsetX: 0, offsetY: 0, color: '#00ffff', blur: 10, fill: true, stroke: true,
      },
    });
    focusArrow.setOrigin(0.5, 0.5);

    // Pulsing animation for focus arrow
    this.tweens.add({
      targets: focusArrow,
      alpha: { from: 0.6, to: 1.0 },
      duration: 600,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    const updateTitleMenuHighlight = () => {
      for (let i = 0; i < menuTexts.length; i++) {
        const text = menuTexts[i];
        if (i === selectedMenu) {
          text.setColor('#00ffff');
          text.setShadow(0, 0, '#00ffff', 15, true, true);
        } else {
          text.setColor(menuDefaults[i].color);
          text.setShadow(0, 0, menuDefaults[i].glowColor, menuDefaults[i].glowBlur, true, true);
        }
      }
      // Position focus arrow to the left of the selected item
      const selectedText = menuTexts[selectedMenu];
      const bounds = selectedText.getBounds();
      focusArrow.setPosition(bounds.x - 25, bounds.y + bounds.height / 2);
    };

    // Initial highlight
    updateTitleMenuHighlight();

    // Input handlers - keyboard navigation
    this.input.keyboard?.on('keydown-DOWN', () => {
      selectedMenu = (selectedMenu + 1) % menuItems.length;
      updateTitleMenuHighlight();
    });
    this.input.keyboard?.on('keydown-UP', () => {
      selectedMenu = (selectedMenu - 1 + menuItems.length) % menuItems.length;
      updateTitleMenuHighlight();
    });
    this.input.keyboard?.on('keydown-TAB', (event: KeyboardEvent) => {
      event.preventDefault();
      selectedMenu = (selectedMenu + 1) % menuItems.length;
      updateTitleMenuHighlight();
    });
    this.input.keyboard?.on('keydown-SPACE', () => menuItems[selectedMenu].select());
    this.input.keyboard?.on('keydown-ENTER', () => menuItems[selectedMenu].select());
    this.input.on('pointerdown', (_pointer: Phaser.Input.Pointer) => {
      // Only start game if clicking on the Press Start text area (with generous bounds)
      const bounds = this.pressStartText.getBounds();
      // Expand bounds slightly for easier clicking
      bounds.x -= 20;
      bounds.y -= 10;
      bounds.width += 40;
      bounds.height += 20;
      if (bounds.contains(_pointer.x, _pointer.y)) {
        this.startGame();
      }
    });

    // Score display - use ScoreService singleton directly for localStorage persistence
    const scoreService = ScoreService.getInstance();
    const topScore = scoreService.getHighScores()[0];
    if (topScore) {
      this.add.text(width / 2, height * 0.88, `TOP: ${topScore.initials} ${topScore.score}`, {
        fontSize: '12px',
        color: '#00ffff',
        fontFamily: '"Press Start 2P", monospace',
        shadow: {
          offsetX: 0,
          offsetY: 0,
          color: '#00ffff',
          blur: 8,
          fill: true,
          stroke: true,
        },
      }).setOrigin(0.5, 0.5);
    }
  }

  update(_time: number, delta: number): void {
    // Pulse the "Press Start" text with neon effect
    // Minimum alpha 0.55 ensures text remains readable at all animation stages
    this.pulseTimer += delta * 0.003;
    const alpha = 0.55 + 0.45 * Math.sin(this.pulseTimer);
    this.pressStartText.setAlpha(alpha);

    // Glow layer pulses in opposite phase for neon flicker effect
    const glowAlpha = 0.15 + 0.3 * Math.sin(this.pulseTimer + 0.5);
    this.pressStartGlow.setAlpha(glowAlpha);

    // Cycle the Press Start color between white and cyan for neon feel
    const colorLerp = 0.5 + 0.5 * Math.sin(this.pulseTimer * 0.7);
    const r = Math.round(colorLerp * 0 + (1 - colorLerp) * 255);
    const g = Math.round(colorLerp * 255 + (1 - colorLerp) * 255);
    const b = 255;
    const colorHex = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
    this.pressStartText.setColor(colorHex);
  }

  private startGame(): void {
    this.safeSceneStart('GameScene');
  }

  /**
   * Clean up keyboard and input listeners when scene is shut down.
   * Prevents memory leaks from accumulated listeners across scene transitions.
   */
  shutdown(): void {
    this.input.keyboard?.removeAllListeners();
    this.input.removeAllListeners();
    // Kill all tweens (star twinkling, alien marching, title floating, etc.)
    this.tweens.killAll();
  }

  private createBackgroundAliens(): void {
    const { width, height } = this.cameras.main;
    const alienY = height * 0.45;

    // Create two rows of marching aliens for more visual impact
    const alienTypes = ['alien-top', 'alien-middle', 'alien-bottom'];
    const rows = [
      { y: alienY - 20, count: 8, offset: 0 },
      { y: alienY + 20, count: 7, offset: 40 },
    ];

    for (const row of rows) {
      for (let i = 0; i < row.count; i++) {
        const typeIndex = i % 3;
        const alien = this.add.image(
          80 + row.offset + i * 80,
          row.y,
          alienTypes[typeIndex]
        );
        alien.setAlpha(0.35);
        alien.setScale(1.2);

        // Marching horizontal tween (side-to-side movement)
        this.tweens.add({
          targets: alien,
          x: alien.x + 50,
          duration: 2000 + (i % 3) * 200,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.easeInOut',
        });
      }
    }
  }
}
